import { BaitItem } from './BaitItem';
import { BaitTypes } from '../constants/BaitTypes';
import { BaitItemOnPlayground } from './BaitItemOnPlayground';
import { BaitStates } from '../constants/BaitStates';
import { FlippedDirections, Snake } from './Snake';

// Random integer from <low, high> interval
const randInt = (low, high) => {
    const newVar = low + Math.floor(Math.random() * (high - low + 1));

    return newVar > high ? high : newVar;
};

const checkIfCellIsInArr = (cell, arr) => {
    const isInArr = !!arr.find((arrCell) => arrCell.i === cell.i && arrCell.j === cell.j);

    return isInArr;
};

const getRandomCells = (num, { w }, rowsFilledCells) => {
    const arr = [];
    const rowsCopy = rowsFilledCells.map((a) => [...a]);
    const numEmptyCells = rowsFilledCells.reduce(
        (accumulator, row) => accumulator + w - row.length,
        0
    );

    num = Math.min(num, numEmptyCells);

    while (arr.length < num) {
        let randIndex = randInt(0, numEmptyCells - 1);

        let i = -1;
        let j = -1;

        rowsCopy.forEach((row, rj) => {
            if (randIndex > w - row.length - 1) {
                randIndex -= w - row.length;
            } else if (j < 0) {
                j = rj;

                if (row.length < 1) {
                    i = randIndex;
                } else {
                    let ci = 0;

                    while (ci < row.length && row[ci].i <= randIndex) {
                        ci += 1;
                        randIndex += 1;

                        if (ci > row.length - 1 && randIndex + 1 < w - 1) randIndex += 1;
                    }
                    if (randIndex < w) {
                        i = randIndex;

                        // Add to exclude further
                        row.splice(i, 0, { i, j });
                    } else {
                        // Leave it for the next row
                        randIndex -= w;
                        j = -1;
                    }
                }
            }
        });

        if (checkIfCellIsInArr({ i, j }, arr)) {
            console.log('Duplicate cell coords detected, skipping');
        }

        if (i > -1 && j > -1 && !checkIfCellIsInArr({ i, j }, arr)) arr.push({ i, j });
    }

    return arr;
};

export class SnakeGameModel {
    // Settings
    #config;
    #displayNextItems = false;
    #maxDuration = -1;
    #maxNumLifes = 0;
    #addItemsSettings;
    #snakeSettings;

    // State
    #startTimestamp = -1;
    #startTimestampStore = -1;
    #startTimestampStoreChanged = false;
    #lastTimestamp = -1;
    #ateItselfTime = -1;
    #startDelay = 2000;
    #ateItselfDelay = 3000;
    #itemsSet = [];
    #itemsOnPlayground = [];
    #itemsOnPlaygroundChanged = false;
    #numLifes = 0;
    #numLifesChanged = false;
    #totalScore = 0;
    #totalScoreChanged = false;
    #ateItself = false;
    #complete = false;
    #snake;
    #deadSnakeCellsToRemove = [];
    #size;
    #gameSessionStartTimestamp = null;
    #bonusScoreIncrement = 0;
    #timesAllAimsCleared = -1;

    constructor(config) {
        const {
            displayNextItems, addItems, snake, maxDuration, numLifes
        } = config;
        const { numPerTime, baitItemsProportion, settings } = addItems;

        this.#config = config;
        this.#addItemsSettings = {
            numPerTime: numPerTime || 1,
            baitItemsProportion,
            settings
        };
        this.#snakeSettings = snake;
        this.#maxDuration = maxDuration;
        this.#numLifes = numLifes;
        this.#maxNumLifes = numLifes;
        this.#displayNextItems = displayNextItems;
    }

    reset() {
        this.#startTimestamp = -1;
        this.#startTimestampStore = -1;
        this.#startTimestampStoreChanged = false;
        this.#itemsOnPlaygroundChanged = false;
        this.#numLifesChanged = false;
        this.#numLifes = this.#maxNumLifes;
        this.#totalScoreChanged = false;
        this.#lastTimestamp = -1;
        this.#ateItself = false;
        this.#ateItselfTime = -1;
        this.#totalScore = 0;
        this.#ateItself = false;
        this.#complete = false;
        this.#gameSessionStartTimestamp = null;
        this.#bonusScoreIncrement = 0;
        this.#timesAllAimsCleared = -1;

        if (this.#snake) this.#snake.reset();
    }

    start(aims, unselected, deathBaits) {
        const { needSize } = this;

        this.#size = needSize;

        if (aims) {
            this.#itemsSet = this.#createSet(aims, unselected, deathBaits);
        }

        if (this.#snake) {
            this.#snake.w = needSize.w;
            this.#snake.h = needSize.h;
        } else {
            this.#snake = new Snake(this.#snakeSettings, needSize);
        }

        this.#snake.readySteady();

        if (!this.#itemsSet || this.#itemsSet.length < 1) {
            console.warn(
                'There is no item to eat.',
                aims && unselected,
                aims,
                unselected,
                this.#itemsSet
            );
            this.#startTimestamp = -1;
            this.#startTimestampStore = -1;
            this.#lastTimestamp = -1;
        } else {
            this.#putNextItemsToPlayground();
            this.#startTimestamp = Date.now();
            this.#lastTimestamp = Date.now();
            this.#gameSessionStartTimestamp = Date.now();
        }
    }

    togglePause() {
        if (this.#startTimestampStore > 0) {
            // RESUME
            this.#startTimestamp = this.#startTimestampStore;
            this.#startTimestampStore = -1;
            this.#startTimestampStoreChanged = true;

            return;
        }

        // PAUSE
        this.#startTimestampStore = this.#startTimestamp;
        this.#startTimestamp = -1;
        this.#lastTimestamp = -1;
        this.#startTimestampStoreChanged = true;
    }

    stop() {
        this.#startTimestamp = -1;
        this.#startTimestampStore = -1;
        this.#lastTimestamp = -1;
        this.#ateItself = false;
        this.#ateItselfTime = -1;
    }

    setLastUpdated() {
        this.#lastTimestamp = Date.now();
        this.#itemsOnPlaygroundChanged = false;
        this.#numLifesChanged = false;
        this.#totalScoreChanged = false;
        this.#startTimestampStoreChanged = false;

        this.#clearEmptyItems();
        this.#snake.resetChanged();
    }

    #createSet(aims, unselected, death) {
        let [countdownAims, countdownUnselected, countdownDeath] = [1, 1, 1];

        if (typeof this.#addItemsSettings.baitItemsProportion === 'string') {
            const [cd0, cd1, cd2] = this.#addItemsSettings.baitItemsProportion
                .split(':')
                .map((s) => parseInt(s, 10));

            countdownAims = typeof cd0 === 'number' ? cd0 : 0;
            countdownUnselected = typeof cd1 === 'number' ? cd1 : 0;
            countdownDeath = typeof cd2 === 'number' ? cd2 : 0;

            // console.log(this, countdownAims, countdownUnselected, countdownDeath, aims, unselected, death);
        }

        let items = [];

        while (countdownAims > 0) {
            items = items.concat(
                aims.map((obj) => {
                    const { viewType } = obj;

                    return new BaitItem(BaitTypes.AIM, viewType);
                })
            );
            countdownAims -= 1;
        }

        while (countdownUnselected > 0) {
            items = items.concat(
                unselected.map((obj) => {
                    const { viewType } = obj;

                    return new BaitItem(BaitTypes.UNSELECTED, viewType);
                })
            );
            countdownUnselected -= 1;
        }

        while (death && countdownDeath > 0) {
            items = items.concat(
                death.map((obj) => {
                    const { viewType } = obj;

                    return new BaitItem(BaitTypes.DEATH, viewType);
                })
            );
            countdownDeath -= 1;
        }

        return items;
    }

    #putNextItemsToPlayground() {
        this.#itemsOnPlayground.splice(0, this.#itemsOnPlayground.length);

        // GENERATE
        const coords = getRandomCells(
            this.#addItemsSettings.numPerTime,
            this.#size,
            this.#snake?.rows
        );

        coords.forEach(({ i, j }) => {
            this.#putAnItemToPlayground(
                this.#itemsSet[randInt(0, this.#itemsSet.length - 1)],
                i,
                j
            );
        });

        this.#itemsOnPlaygroundChanged = true;
        this.#timesAllAimsCleared += 1;
    }

    #putAnItemToPlayground(item, i, j) {
        const { type } = item;

        const { settings } = this.#addItemsSettings;
        const itemProps = settings[type];

        this.#itemsOnPlayground.push(
            new BaitItemOnPlayground(item, itemProps, i, j)
        );
    }

    #clearEmptyItems() {
        this.#itemsOnPlayground = this.#itemsOnPlayground.filter(
            (item) => item.state !== BaitStates.EMPTY
        );
    }

    invalidate(t) {
        if (this.#complete || this.#startTimestamp < 0) return;
        if (t - this.#startTimestamp < this.#startDelay) return;

        this.#deadSnakeCellsToRemove = [];

        if (this.#ateItself) {
            if (t - this.#ateItselfTime > this.#ateItselfDelay) {
                this.#ateItself = false;
                this.#ateItselfTime = -1;
                this.#deadSnakeCellsToRemove = [...this.#snake.cells];

                this.#snake.reset();
                this.#snake.readySteady();
            }

            return;
        }

        if (!this.#snake.running) this.#snake.go();

        const [head0, head1] = this.#snake.cells;
        const { i: h0i, j: h0j } = head0;
        const { i: h1i, j: h1j } = head1;
        let eatItemAction = null;
        let life = 0;
        const aimBaitsLeftOnTheField = [];
        const deathBaitsLeftOnTheField = [];

        this.#itemsOnPlayground.forEach((item) => {
            if (item.type === BaitTypes.AIM) {
                aimBaitsLeftOnTheField.push(item);
            }
            if (item.type === BaitTypes.DEATH) {
                deathBaitsLeftOnTheField.push(item);
            }
        });

        if (aimBaitsLeftOnTheField.length <= 0 && deathBaitsLeftOnTheField.length) {
            deathBaitsLeftOnTheField.forEach((item) => {
                item.remove();
            });
            this.#itemsOnPlaygroundChanged = true;
        } else if (this.#itemsOnPlayground.length > 0) {
            let changed = false;

            this.#itemsOnPlayground.forEach((item) => {
                item.resetChanged();

                const alive = item.isAlive(t);

                if (alive) {
                    // CHECK IF EATEN
                    if (
                        (item.row === h0j && item.col === h0i)
                        || (item.row === h1j && item.col === h1i)
                    ) {
                        const eaten = item.eat();

                        if (eaten) {
                            eatItemAction = item.action;
                        }
                    }
                }

                changed = item.changed || changed;
            });

            this.#itemsOnPlaygroundChanged = changed || this.#itemsOnPlaygroundChanged;
        } else {
            this.#putNextItemsToPlayground();
        }

        if (eatItemAction) {
            const { points, speedIncrement } = eatItemAction;

            this.#snake.incrementBody = points;
            this.#snake.incrementSpeed = speedIncrement;
        }

        this.#snake.invalidate(t);

        if (!this.#snake.isAlive(t)) {
            this.#ateItself = true;
            this.#ateItselfTime = t;
            eatItemAction = this.#config.selfEating.action;
        }

        if (eatItemAction) {
            const { points, bonusIncrement } = eatItemAction;

            const applyIncrement = points > 0;

            if (applyIncrement) {
                this.#totalScore += points + this.#bonusScoreIncrement;
            } else {
                this.#totalScore += points;
            }
            if (this.#totalScore <= 0) {
                this.#totalScore = 0;
            }
            this.#totalScoreChanged = true;

            if (bonusIncrement) {
                this.#bonusScoreIncrement += bonusIncrement;
            }
        }

        if (eatItemAction) {
            life = eatItemAction.life;
        }

        if (life !== 0) {
            this.#numLifes += life;
            if (this.#numLifes <= 0) {
                this.#numLifes = 0;
                this.#complete = true;
            }
            this.#numLifesChanged = true;
        }
    }

    get needSize() {
        let { w, h } = this.#config.sizes;
        const { portrait } = this.#config.sizes;

        if (portrait && window.innerHeight > window.innerWidth) {
            ({ w, h } = portrait);
        }

        return { w, h };
    }

    setDirection(value, flipped = false) {
        this.#snake.direction = flipped ? FlippedDirections[value] : value;
    }

    get direction() {
        return this.#snake.direction;
    }

    get snakeCells() {
        return this.#snake.cells;
    }

    get snakeHeadLength() {
        return this.#snake.head;
    }

    get snakeTailLength() {
        return this.#snake.tail;
    }

    get snakeRemovedCells() {
        return this.#snake.lastCellsRemoved
            ? [...this.#snake.lastCellsRemoved, ...this.#deadSnakeCellsToRemove]
            : this.#deadSnakeCellsToRemove;
    }

    get itemsOnPlayground() {
        return this.#itemsOnPlayground;
    }

    get itemsOnPlaygroundChanged() {
        return this.#itemsOnPlaygroundChanged;
    }

    get size() {
        return this.#size;
    }

    get totalScore() {
        return this.#totalScore;
    }

    get complete() {
        return this.#complete;
    }

    get ateItself() {
        return this.#ateItself;
    }

    get inProgress() {
        return (
            !this.#complete
            && (this.#startTimestamp > 0 || this.#startTimestampStore > 0)
        );
    }

    get isPlaying() {
        return !this.#complete && this.#startTimestamp > 0;
    }

    get isPlayingChanged() {
        return !this.#complete && this.#startTimestampStoreChanged;
    }

    get lifes() {
        return {
            value: this.#numLifes,
            of: this.#maxNumLifes
        };
    }

    get lifesChanged() {
        return this.#numLifesChanged;
    }

    get score() {
        return this.#totalScore;
    }

    get scoreChanged() {
        return this.#totalScoreChanged;
    }

    get lastUpdated() {
        return this.#lastTimestamp;
    }

    get timesAllAimsCleared() {
        return this.#timesAllAimsCleared;
    }

    // eslint-disable-next-line class-methods-use-this
    get fps() {
        return 30;
    }

    getResult(resultsMap) {
        const ids = Object.keys(resultsMap);
        const results = Object.values(resultsMap);

        const i = results.findIndex((r) => r.check && r.check(this.totalScore));
        const id = i > -1 ? ids[i] : 'worst';
        const result = {
            id,
            score: this.totalScore,
            startedAt: this.#gameSessionStartTimestamp,
            ...resultsMap[id]
        };

        delete result.check;

        return result;
    }
}
